import { useState, type ReactElement, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "./style.less";
import Table from "../../shared/layout/table";
import { type TrialDataInterface } from "./types";
import { TrialListColumns } from "./constant";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../shared/utils/translationUtils";
import TrailModal from "../../shared/layout/Modal";
import type { monitorModaldata } from "../../shared/layout/types";
import useDownloadWorkOrder from "../../shared/custom-hooks/useDownloadWorkOrder";
import { Loader } from "../../shared/components/Loader";
import ProtocolDrawer from "../../shared/layout/ProtocolDrawer";
import TrialPreviewDrawer from "../../shared/layout/TrialPreviewDrawer";
import {
  ExportEvents,
  TrialCreationAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { toast, ToastContainer } from "react-toastify";
import { TrialReportDrawer } from "../../pages/new-trail/TrialReportDrawer";
import PrescriptionExporterMfeLauncher from "../../shared/components/ConnectFiles/PrescriptionExporterModal";
import AgOperationDataLoader from "../new-trail/SummaryComponent/AgOperationDataLoader";
import { useFeatureFlags } from "../../context/LDFlagsContext";
import i18n from "../../i18n";
import {
  fetchTrials,
  fetchTrialById,
  generateReport,
  deleteTrial,
  markTrialComplete,
  fetchTrialFilters,
  type TrialFiltersApiResponse,
  type TrialListFilterParams,
} from "./trialService";

import TrialsPageHeader from "./pageHeader";
import SearchAndFilter from "./searchAndFilter";
import TableEmptyState from "./tableEmptyState";
import CompletionConfirmationModal from "./completionConfirmationModal";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import { useProperty } from "../../shared/custom-hooks/useProperties";
import { useWorkspace } from "../../shared/custom-hooks/useWorkspace";
import { useAppState } from "../../lib/appContext/AppContext";
import { useAuth } from "../../lib/auth/AuthContext";
import type { TrialFiltersState } from "./TrialFiltersPanel";
import { TrialSummaryInsightsPanel } from "./insights/TrialSummaryInsightsPanel";
import { resolveHydratedFarmValue } from "./farmValueUtils";

const SEARCH_DEBOUNCE_DELAY_MS = 500;

const EMPTY_TRIAL_FILTERS: TrialFiltersState = {
  status: [],
  type: [],
  crop: [],
  fields: [],
  country: [],
  timeRange: {},
  creator: [],
  assignee: [],
};

/**
 * Creates the main component for displaying and managing trials.
 */
const TrialsList = (): ReactElement => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [trialData, setTrialData] = useState<TrialDataInterface[]>([]);
  const { router, orgId, workspaceId, farmId } = useTrialsRouter();
  const [searchText, setSearchText] = useState<string>("");
  const [completionFilter, setCompletionFilter] = useState<string>("all");
  const [trialFilters, setTrialFilters] =
    useState<TrialFiltersState>(EMPTY_TRIAL_FILTERS);

  const [trialFilterOptions, setTrialFilterOptions] =
    useState<TrialFiltersApiResponse | null>(null);
  const [trialFilterOptionsLoading, setTrialFilterOptionsLoading] =
    useState<boolean>(false);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isLoading, setLoading] = useState<boolean>(false);
  const { downloadISOXMLOrder, downloadmonitorModalForm } =
    useDownloadWorkOrder();
  const [modalOpen, setModalOpen] = useState(false);
  const [fieldIds, setfieldIds] = useState("");
  const [trailId, setTrialId] = useState("");
  const [protocolId, setProtocolId] = useState("");
  const [open, setOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTrialInfo, setSelectedTrialInfo] = useState<any>(null);
  const [exportAgOperationIds, setExportAgOperationIds] = useState<string[]>(
    []
  );
  const [agLoaderOpen, setAgLoaderOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reportData, setReportData] = useState<
    { uuid: string | null; reportId: string | null } | undefined
  >(undefined);
  const [, setDrawerLoading] = useState(false);
  const [drawerView, setDrawerView] = useState<"share" | "tree">("share");
  const [treeCheckedKeys, setTreeCheckedKeys] = useState<string[]>([]);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionTrialId, setCompletionTrialId] = useState<string | null>(
    null
  );
  const [completionStatus, setCompletionStatus] = useState<boolean | null>(
    null
  );
  // Trial row the preview drawer was opened from, and its open state.
  const [previewTrial, setPreviewTrial] = useState<TrialDataInterface | null>(
    null
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const hasInitializedSearch = useRef(false);

  const extraHeaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const { enableNewWorkorderExport } = useFeatureFlags();

  const trackTrialsEvents = useTrackingEvent({
    page: "Trials Page",
    group: "Trials",
    module: "Trials",
  });

  const property = useProperty(farmId);
  const workspace = useWorkspace(workspaceId);
  const { stateValue, setStateValue } = useAppState();

  const mapStatusFiltersToExecutionStatus = (statuses: string[]): string[] => {
    const statusMap: Record<string, string> = {
      Activated: "activated",
      Ready: "ready",
      Draft: "draft",
    };

    return statuses
      .map((status) => statusMap[status])
      .filter((status): status is string => Boolean(status));
  };

  const mapTrialFiltersToApiParams = (
    filters: TrialFiltersState
  ): TrialListFilterParams => {
    return {
      protocol_type: filters.type,
      crop: filters.crop,
      execution_status: mapStatusFiltersToExecutionStatus(filters.status),
      country: filters.country,
      field_id: filters.fields,
      created_by: filters.creator,
      assignee_id: filters.assignee,
      date_from: filters.timeRange.from,
      date_to: filters.timeRange.to,
    };
  };

  useEffect(() => {
    const hydratedFarmValue = resolveHydratedFarmValue(
      stateValue?.farmValue,
      property.data
    );

    if (hydratedFarmValue) {
      setStateValue((prevState: any) => ({
        ...prevState,
        farmValue: hydratedFarmValue,
      }));
    }
  }, [stateValue?.farmValue?.id, stateValue?.farmValue?.name, property]);

  useEffect(() => {
    if (
      workspace.data?.name &&
      (!stateValue?.workspaceValue?.id ||
        (stateValue?.orgValue?.workspace_id &&
          stateValue?.workspaceValue?.id &&
          stateValue?.workspaceValue?.id !==
            stateValue?.orgValue?.workspace_id))
    ) {
      setStateValue((prevState: any) => ({
        ...prevState,
        workspaceValue: {
          name: workspace.data?.name ?? "",
          id: workspace.data?.id ?? "",
        },
      }));
    }
  }, [stateValue?.orgValue?.workspace_id, workspace]);

  const fetchTrialFilterOptions = async (): Promise<void> => {
    if (!orgId || !farmId) return;

    setTrialFilterOptionsLoading(true);

    try {
      const filters = await fetchTrialFilters(orgId, farmId);
      setTrialFilterOptions(filters);
    } catch (error) {
      console.error("Error fetching trial filter options:", error);
      setTrialFilterOptions(null);
    } finally {
      setTrialFilterOptionsLoading(false);
    }
  };

  const handleGenerateReport = async (
    trialId: string[],
    fieldIds: string[]
  ): Promise<{ uuid: string; reportId: string }> => {
    try {
      const response = await generateReport(trialId, fieldIds);
      const { uuid, reportId } = response;

      if (!uuid || !reportId) {
        throw new Error("No report UUID or reportId received in response");
      }

      setReportData(response);
      return response;
    } catch (error) {
      setReportData({ uuid: null, reportId: null });
      throw error;
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleShareReport = async (trialInfo: any): Promise<void> => {
    setSelectedTrialInfo(trialInfo);

    if (!reportData?.uuid || selectedTrialInfo?.id !== trialInfo.id) {
      await handleGenerateReport([trialInfo.id], trialInfo.field_ids);
    }

    setDrawerVisible(true);
  };

  const hideDrawer = (): void => {
    setDrawerVisible(false);
    setSelectedTrialInfo(null);
    setReportData(undefined);
    setDrawerView("share");
  };

  const handleOk = (data: monitorModaldata, fielFormdData: any): void => {
    if (
      data.selectedmonitorBrand === "isoxml" ||
      data.selectedmonitorModel === "isoxml"
    ) {
      downloadISOXMLOrder(fielFormdData, trailId).catch(() => {
        console.error("Error While Fetching API..!");
      });
    } else {
      downloadmonitorModalForm(data, trailId, fieldIds).catch(() => {
        console.error("Erroe While Fetching API..!");
      });
    }

    setModalOpen(false);
  };

  const handleCancel = (): void => {
    setModalOpen(false);
    setTrialId("");
    setfieldIds("");
    setExportAgOperationIds([]);
    setErrorMessage("");
  };

  const hideAgLoader = (): void => {
    setAgLoaderOpen(false);
    setErrorMessage("");
    setExportAgOperationIds([]);
    setTrialId("");
    setfieldIds("");
  };

  const showExportModal = (
    trialId: string,
    fieldId: string,
    protocolId?: string
  ): void => {
    setModalOpen(true);
    void trackTrialsEvents({
      event: ExportEvents.TRIAL_DATA_EXPORTED,
      export_source: "button",
      trial_id: trialId,
      protocol_id: protocolId,
      field_id: fieldId,
    });
  };

  const openModal = (
    trailId: string,
    fieldIds: string,
    trialInfo: any
  ): void => {
    setfieldIds(fieldIds);
    setTrialId(trailId);
    setExportAgOperationIds(
      Object.values(trialInfo?.ag_operations || {})
        .flat()
        .filter((value): value is string => typeof value === "string")
    );

    if (enableNewWorkorderExport) {
      setAgLoaderOpen(true);
    } else {
      showExportModal(trailId, fieldIds, trialInfo?.protocol_id);
    }
  };

  useEffect(() => {
    if (!agLoaderOpen) return;

    setErrorMessage("");

    if (!trailId) {
      setErrorMessage(formatTranslation(t("trials.missingAgOpIdError")));
      return;
    }

    let cancelled = false;

    void (async () => {
      let latestTrial: any = null;

      try {
        latestTrial = await fetchTrialById(trailId);
      } catch (error) {
        console.error("Error fetching trial:", error);
      }

      if (cancelled) return;

      const fetchedAgOperationIds = Object.values(
        latestTrial?.ag_operations ?? {}
      )
        .flat()
        .filter((value): value is string => typeof value === "string");

      setExportAgOperationIds(fetchedAgOperationIds);

      if ((latestTrial?.ag_operation_status ?? "") !== "") {
        setErrorMessage(formatTranslation(t("trials.agOpCreationfailedError")));
      } else if (fetchedAgOperationIds.length === 0) {
        setErrorMessage(formatTranslation(t("trials.missingAgOpIdError")));
      } else {
        setErrorMessage("");
        setAgLoaderOpen(false);
        showExportModal(trailId, fieldIds, latestTrial?.protocol_id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agLoaderOpen]);

  const normalizeTrialsResponse = (
    response: any
  ): { trials: TrialDataInterface[]; count: number } => {
    if (Array.isArray(response)) {
      return {
        trials: response,
        count: response.length,
      };
    }

    return {
      trials: response?.trials || [],
      count: response?.count || 0,
    };
  };

  const fetchData = async (
    pagination: any,
    resetFilter: boolean = false,
    searchValue: string = searchText,
    statusOverride?: string,
    filtersOverride?: TrialFiltersState
  ): Promise<any> => {
    setLoading(true);

    try {
      const activeFilters = resetFilter
        ? EMPTY_TRIAL_FILTERS
        : filtersOverride ?? trialFilters;

      const trialsResponse = await fetchTrials(
        orgId ?? "",
        farmId ?? "",
        pagination.current,
        pagination.pageSize,
        searchValue,
        resetFilter ? "all" : statusOverride ?? completionFilter,
        mapTrialFiltersToApiParams(activeFilters)
      );

      const normalizedResponse = normalizeTrialsResponse(trialsResponse);

      setTrialData(normalizedResponse.trials);
      setPagination({
        ...pagination,
        total: normalizedResponse.count,
      });
      setLoading(false);

      return normalizedResponse.trials;
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const handlePagination = async (pagination: any): Promise<void> => {
    setPagination(pagination);
    await fetchData(pagination);
  };

  const handleSearchTextChange = (value: string): void => {
    setSearchText(value);
    setPagination((currentPagination) => ({
      ...currentPagination,
      current: 1,
    }));
  };

  const handleDeleteTrialData = async (id: string): Promise<any> => {
    if (!id) {
      console.error("Cannot delete trial: trial ID is undefined");
      return;
    }

    await deleteTrial(id);
    await fetchData(pagination);
    await fetchTrialFilterOptions();
    void queryClient.invalidateQueries({ queryKey: ["trialInsights"] });
  };

  const openDrawer = (protocolId: string): void => {
    if (protocolId) {
      setProtocolId(protocolId);
      setOpen(true);
    }
  };

  const onClose = (): void => {
    setOpen(false);
  };

  /**
   * Opens the trial preview drawer for the clicked row. The row record is kept
   * so the drawer can paint immediately while it refetches the trial.
   * @param trial The trial row the preview was opened from.
   */
  const openTrialPreview = (trial: TrialDataInterface): void => {
    if (!trial?.id) return;
    setPreviewTrial(trial);
    setPreviewOpen(true);
    void trackTrialsEvents({
      event: TrialCreationAmplitudeEvent.TRIAL_PREVIEW,
      originAction: "Trial Preview",
      trial_id: trial.id,
    });
  };

  /**
   * Closes the trial preview drawer. The table's pagination, filters and search
   * are untouched, so the list is exactly as the user left it.
   */
  const closeTrialPreview = (): void => {
    setPreviewOpen(false);
    setPreviewTrial(null);
  };

  /**
   * "Open Trial" in the preview's Navigate-to section: closes the preview and
   * routes to the full trial page.
   * @param id The trial to open.
   */
  const handleOpenTrialFromPreview = (id: string): void => {
    closeTrialPreview();
    router.goToTrialsEditPage(
      undefined,
      id,
      false,
      previewTrial?.completion === true
    );
  };

  /**
   * toggles the completion status of a trial by sending a PATCH request to the server with the updated completion value.
   * Upon successful update, it shows a success toast message and updates the local trial data state to reflect the change. If an error occurs, it shows an error toast message.
   * @param checked The new completion status to set for the trial.
   * @param trialId The ID of the trial for which the completion status is being toggled.
   * @returns A promise that resolves when the completion status has been toggled and the local state has been updated.
   */
  const toggleCompletion = async (
    checked: boolean,
    trialId: string
  ): Promise<any> => {
    if (!trialId) {
      console.error("Cannot toggle completion: trial ID is undefined");
      return;
    }

    try {
      const response = await markTrialComplete(trialId, checked, user?.id);

      if (response) {
        const trialQueryKey = ["trial", trialId];
        // A pre-toggle GET must not overwrite the confirmed activation status.
        await queryClient.cancelQueries({
          queryKey: trialQueryKey,
          exact: true,
        });
        queryClient.setQueryData<TrialDataInterface>(
          trialQueryKey,
          (cachedTrial: TrialDataInterface | undefined) =>
            cachedTrial
              ? {
                  ...cachedTrial,
                  completion: checked,
                  execution_status: checked ? "activated" : "ready",
                }
              : undefined
        );
        await queryClient.invalidateQueries({
          queryKey: trialQueryKey,
          exact: true,
        });
        toast.success(formatTranslation(t("trials.completionSuccess")));
        await fetchData(pagination);
        void queryClient.invalidateQueries({ queryKey: ["trialInsights"] });

        void trackTrialsEvents({
          event: TrialCreationAmplitudeEvent.TRIAL_STATUS_TOGGLE,
          originAction: `trial-completion-set-${checked ? "true" : "false"}`,
          trial_id: trialId,
          is_activated: checked,
        });
      } else {
        toast.error(formatTranslation(t("trials.completionError")));
      }
    } catch (error) {
      console.error("Failed to update trial activation status", error);
      toast.error(formatTranslation(t("trials.completionError")));
    }
  };

  const handleToggleCompletion = (checked: boolean, trialId: string): void => {
    setCompletionStatus(checked);

    if (checked) {
      const trial = trialData.find((trial) => trial.id === trialId);
      const isOnSummaryStep =
        (trial?.completed_step != null && Number(trial.completed_step) >= 3) ||
        trial?.is_exportable === true;

      if (isOnSummaryStep) {
        void toggleCompletion(checked, trialId);
      } else {
        setCompletionTrialId(trialId);
        setCompletionModalOpen(true);
      }
    } else {
      setCompletionTrialId(trialId);
      setCompletionModalOpen(true);
    }
  };

  const handleCompletionModalCancel = (): void => {
    setCompletionModalOpen(false);
    setCompletionTrialId(null);
    setCompletionStatus(null);
  };

  const handleCompletionModalProceed = (): void => {
    if (!completionStatus) {
      void toggleCompletion(false, completionTrialId as string);
    } else {
      router.goToTrialsEditPage(undefined, completionTrialId as string);
    }

    handleCompletionModalCancel();
  };

  const trialColumns = TrialListColumns(
    handleDeleteTrialData,
    openModal,
    openDrawer,
    handleToggleCompletion,
    handleShareReport,
    stateValue?.farmValue?.name
  );

  const handleNavigate = (): void => {
    router.goToTrialsCreatePage();
  };

  useEffect(() => {
    const fetchDataAndHandleError = async (): Promise<void> => {
      try {
        await fetchData(pagination);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchDataAndHandleError().catch((error) => {
      console.error("Error handling fetchDataAndHandleError:", error);
      return error;
    });
  }, [orgId, farmId]);

  useEffect(() => {
    void fetchTrialFilterOptions();
  }, [orgId, farmId]);

  useEffect(() => {
    if (!hasInitializedSearch.current) {
      hasInitializedSearch.current = true;
      return;
    }

    const updatedPagination = {
      ...pagination,
      current: 1,
    };

    const timer = setTimeout(() => {
      void fetchData(updatedPagination, false, searchText);
    }, SEARCH_DEBOUNCE_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  const applyFilter = (reset?: boolean): void => {
    if (reset) {
      setCompletionFilter("all");
      setTrialFilters(EMPTY_TRIAL_FILTERS);
    }

    setDropdownVisible(false);
    void fetchData(pagination, reset);
    void trackTrialsEvents({
      event: TrialCreationAmplitudeEvent.TRIAL_FILTER,
      filter_type: "status",
      filter_value: reset ? "all" : completionFilter,
    });
  };

  const applyTrialFilters = (filters: TrialFiltersState): void => {
    setTrialFilters(filters);

    const updatedPagination = {
      ...pagination,
      current: 1,
    };

    setPagination(updatedPagination);

    void fetchData(updatedPagination, false, searchText, "all", filters);
    const filterTypes = Object.entries(filters)
      .filter(([, value]) =>
        Array.isArray(value)
          ? value.length > 0
          : Boolean(value.from || value.to)
      )
      .map(([filterType]) => filterType);
    void trackTrialsEvents({
      event: TrialCreationAmplitudeEvent.TRIAL_FILTER,
      filter_type: filterTypes,
    });
  };

  return (
    <div className="create-trail-main">
      {isLoading && <Loader mask message="" />}

      {fieldIds && trailId && !enableNewWorkorderExport && (
        <TrailModal
          open={modalOpen}
          handleOk={handleOk}
          handleCancel={handleCancel}
          fieldId={fieldIds}
          trialId={trailId}
        />
      )}

      {fieldIds &&
        trailId &&
        enableNewWorkorderExport &&
        modalOpen &&
        exportAgOperationIds.length > 0 &&
        errorMessage === "" && (
          <PrescriptionExporterMfeLauncher
            isOpen={modalOpen}
            onClose={handleCancel}
            agOperationIds={exportAgOperationIds}
            userLanguage={i18n.language}
          />
        )}

      {enableNewWorkorderExport && agLoaderOpen && (
        <AgOperationDataLoader
          open={agLoaderOpen}
          onClose={hideAgLoader}
          errorMessage={errorMessage}
        />
      )}

      {protocolId && (
        <ProtocolDrawer
          protocolId={protocolId}
          open={open}
          onClose={onClose}
          extraHeaders={extraHeaders}
          showLinkedTrials
        />
      )}
      {previewTrial?.id && (
        <TrialPreviewDrawer
          trialId={previewTrial.id}
          open={previewOpen}
          onClose={closeTrialPreview}
          fallbackTrial={previewTrial}
          orgId={orgId ?? undefined}
          propertyId={farmId ?? undefined}
          onOpenTrial={handleOpenTrialFromPreview}
          extraHeaders={extraHeaders}
        />
      )}
      {selectedTrialInfo && (
        <TrialReportDrawer
          open={drawerVisible}
          onClose={hideDrawer}
          trialId={selectedTrialInfo.id}
          fieldIds={selectedTrialInfo.field_ids}
          reportData={reportData}
          onGenerateReport={handleGenerateReport}
          propertyId={selectedTrialInfo.property_id}
          noOfFields={selectedTrialInfo.field_ids?.length}
          noOfTrails={selectedTrialInfo.field_ids?.length}
          drawerView={drawerView}
          onEdit={() => {
            setDrawerView("tree");
          }}
          onTreeBack={() => {
            setDrawerView("share");
          }}
          treeCheckedKeys={treeCheckedKeys}
          setTreeCheckedKeys={setTreeCheckedKeys}
        />
      )}

      <TrialsPageHeader isLoading={isLoading} handleNavigate={handleNavigate} />

      <div className="body-content">
        <SearchAndFilter
          searchText={searchText}
          setSearchText={handleSearchTextChange}
          completionFilter={completionFilter}
          setCompletionFilter={setCompletionFilter}
          applyFilter={applyFilter}
          dropdownVisible={dropdownVisible}
          setDropdownVisible={setDropdownVisible}
          trialData={trialData}
          trialFilters={trialFilters}
          setTrialFilters={setTrialFilters}
          applyTrialFilters={applyTrialFilters}
          trialFilterOptions={trialFilterOptions}
          trialFilterOptionsLoading={trialFilterOptionsLoading}
        />

        <TrialSummaryInsightsPanel
          orgId={orgId ?? ""}
          farmId={farmId ?? ""}
          search={searchText}
          completionFilter={completionFilter}
          filters={mapTrialFiltersToApiParams(trialFilters)}
        />

        <div className="table-sec">
          <Table
            columns={trialColumns}
            dataSource={trialData}
            pagination={
              pagination.total > pagination.pageSize
                ? { ...pagination, position: ["bottomCenter"] }
                : false
            }
            onChange={(pagination: any): void => {
              void handlePagination(pagination);
            }}
            onRow={(record: TrialDataInterface) => ({
              onClick: () => {
                openTrialPreview(record);
              },
              tabIndex: 0,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openTrialPreview(record);
                }
              },
            })}
            locale={{
              emptyText: (
                <TableEmptyState isLoading={isLoading} trialData={trialData} />
              ),
            }}
            rowKey="id"
          />

          <ToastContainer />

          <CompletionConfirmationModal
            completionModalOpen={completionModalOpen}
            handleCompletionModalCancel={handleCompletionModalCancel}
            handleCompletionModalProceed={handleCompletionModalProceed}
            completionStatus={completionStatus}
          />
        </div>
      </div>
    </div>
  );
};

export default TrialsList;
